// built-from: /knowledge-files-view v1 impl:knowledge-files-view role:implementation
// useKnowledgeFiles - the API boundary for the engagement's knowledge tree
// (feature 090-engagement-knowledge-tree).
//
// The Files view reaches the API ONLY through this hook (constitution III:
// never `fetch` inside a component). Every call resolves a typed result and
// never throws: a transport failure degrades to `{ ok: false }` so the view can
// say what failed without a crash. Nothing here decides what to render.

import { useCallback, useEffect, useRef, useState } from 'react';
import { splitPageNotes } from '../utils/knowledgePages';
import { useEngagement } from './useEngagement';
import { useRefreshOnReturn } from './useRefreshOnReturn';

export interface KnowledgeFileEntry {
  path: string;
  etag: string;
}

export interface KnowledgeFile {
  path: string;
  content: string;
  // 093: a page ArcPilot keeps current comes back in two parts - `generated`,
  // which the projection rewrites as the engagement changes, and `notes`, the
  // consultant's own section. A reply that carries only `content` is split the
  // same way, so the view never has to guess.
  generated: string;
  notes: string;
  etag: string;
  managed: boolean;
  meta: Record<string, unknown> | null;
  // 092: a file kept exactly as it was uploaded (a PDF, an image) has no text
  // to read. The read returns its description only; the bytes come from the
  // raw URL below.
  binary: boolean;
  contentType: string;
  size: number;
}

export interface ActivityFiling {
  status: 'filed' | 'failed';
  path: string;
  message?: string;
}

export type KnowledgeListResult =
  | { ok: true; files: KnowledgeFileEntry[] }
  | { ok: false; reason: 'unavailable' };

export type KnowledgeOpenResult =
  | { ok: true; file: KnowledgeFile }
  | { ok: false; reason: 'missing' | 'unavailable' };

export type KnowledgeSaveResult =
  | { ok: true; etag: string }
  | { ok: false; reason: 'conflict'; current: { content: string; etag: string } }
  | { ok: false; reason: 'unavailable' };

export type ActivityRefileResult =
  | { ok: true; filing: ActivityFiling }
  | { ok: false; reason: 'unavailable' };

export type KnowledgeListStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseKnowledgeFilesReturn {
  files: KnowledgeFileEntry[];
  listStatus: KnowledgeListStatus;
  refresh: () => Promise<KnowledgeListResult>;
  openFile: (path: string) => Promise<KnowledgeOpenResult>;
  saveFile: (path: string, content: string, etag: string) => Promise<KnowledgeSaveResult>;
  saveNotes: (path: string, notes: string, etag: string) => Promise<KnowledgeSaveResult>;
  refileActivity: (activityId: string) => Promise<ActivityRefileResult>;
  rawFileUrl: (path: string) => string;
}

// Paths are relative and segmented ("wiki/sources/act-1.md"). Encode each
// segment so a space or a hash survives the URL, and keep the separators.
export function encodeKnowledgePath(path: string): string {
  return path
    .split('/')
    .filter((segment) => segment.length > 0)
    .map((segment) => encodeURIComponent(segment))
    .join('/');
}

function readEntries(body: unknown): KnowledgeFileEntry[] {
  const files = (body as { files?: unknown } | null)?.files;
  if (!Array.isArray(files)) return [];
  return files.flatMap((candidate) => {
    const entry = candidate as { path?: unknown; etag?: unknown };
    if (typeof entry?.path !== 'string' || !entry.path) return [];
    return [{ path: entry.path, etag: typeof entry.etag === 'string' ? entry.etag : '' }];
  });
}

function readFile(body: unknown): KnowledgeFile | null {
  const record = body as {
    path?: unknown;
    content?: unknown;
    generated?: unknown;
    notes?: unknown;
    etag?: unknown;
    managed?: unknown;
    meta?: unknown;
    binary?: unknown;
    contentType?: unknown;
    size?: unknown;
  } | null;
  if (!record || typeof record.path !== 'string') return null;
  const content = typeof record.content === 'string' ? record.content : '';
  const parts = splitPageNotes(content);
  return {
    path: record.path,
    content,
    generated: typeof record.generated === 'string' ? record.generated : parts.generated,
    notes: typeof record.notes === 'string' ? record.notes : parts.notes,
    etag: typeof record.etag === 'string' ? record.etag : '',
    managed: record.managed === true,
    meta: record.meta && typeof record.meta === 'object' && !Array.isArray(record.meta)
      ? (record.meta as Record<string, unknown>)
      : null,
    binary: record.binary === true,
    contentType: typeof record.contentType === 'string' ? record.contentType : '',
    size: typeof record.size === 'number' ? record.size : 0,
  };
}

function readFiling(body: unknown): ActivityFiling | null {
  const filing = (body as { filing?: unknown } | null)?.filing as {
    status?: unknown;
    path?: unknown;
    message?: unknown;
  } | undefined;
  if (!filing || (filing.status !== 'filed' && filing.status !== 'failed')) return null;
  return {
    status: filing.status,
    path: typeof filing.path === 'string' ? filing.path : '',
    message: typeof filing.message === 'string' ? filing.message : undefined,
  };
}

async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export function useKnowledgeFiles(): UseKnowledgeFilesReturn {
  const { currentEngagementId } = useEngagement();
  const [files, setFiles] = useState<KnowledgeFileEntry[]>([]);
  const [listStatus, setListStatus] = useState<KnowledgeListStatus>('idle');
  const liveRef = useRef(true);
  const activeBase = useRef<string | null>(null);
  const listRequest = useRef(0);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const base = currentEngagementId
    ? `/api/engagements/${encodeURIComponent(currentEngagementId)}`
    : null;
  activeBase.current = base;

  const refresh = useCallback(async (): Promise<KnowledgeListResult> => {
    if (!base) {
      setListStatus('error');
      return { ok: false, reason: 'unavailable' };
    }
    const request = ++listRequest.current;
    setListStatus('loading');
    try {
      const res = await fetch(`${base}/files`);
      if (!res.ok) {
        if (liveRef.current && activeBase.current === base && request === listRequest.current) setListStatus('error');
        return { ok: false, reason: 'unavailable' };
      }
      const entries = readEntries(await readJson(res));
      if (liveRef.current && activeBase.current === base && request === listRequest.current) {
        setFiles(entries);
        setListStatus('ready');
      }
      return { ok: true, files: entries };
    } catch {
      if (liveRef.current && activeBase.current === base && request === listRequest.current) setListStatus('error');
      return { ok: false, reason: 'unavailable' };
    }
  }, [base]);

  useEffect(() => {
    listRequest.current += 1;
    setFiles([]);
    setListStatus(base ? 'idle' : 'error');
  }, [base]);

  useRefreshOnReturn(Boolean(base), async () => {
    if (activeBase.current === base) await refresh();
  });

  const openFile = useCallback(async (path: string): Promise<KnowledgeOpenResult> => {
    if (!base || !path) return { ok: false, reason: 'unavailable' };
    try {
      const res = await fetch(`${base}/files/${encodeKnowledgePath(path)}`);
      if (res.status === 404) return { ok: false, reason: 'missing' };
      if (!res.ok) return { ok: false, reason: 'unavailable' };
      const file = readFile(await readJson(res));
      if (!file) return { ok: false, reason: 'unavailable' };
      return { ok: true, file };
    } catch {
      return { ok: false, reason: 'unavailable' };
    }
  }, [base]);

  const saveFile = useCallback(async (
    path: string,
    content: string,
    etag: string,
  ): Promise<KnowledgeSaveResult> => {
    if (!base || !path) return { ok: false, reason: 'unavailable' };
    try {
      const res = await fetch(`${base}/files/${encodeKnowledgePath(path)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'If-Match': etag },
        body: JSON.stringify({ content }),
      });
      const body = await readJson(res);
      if (res.status === 409) {
        const current = (body as { current?: { content?: unknown; etag?: unknown } } | null)?.current;
        return {
          ok: false,
          reason: 'conflict',
          current: {
            content: typeof current?.content === 'string' ? current.content : '',
            etag: typeof current?.etag === 'string' ? current.etag : '',
          },
        };
      }
      const nextEtag = (body as { etag?: unknown } | null)?.etag;
      if (!res.ok || typeof nextEtag !== 'string') return { ok: false, reason: 'unavailable' };
      return { ok: true, etag: nextEtag };
    } catch {
      return { ok: false, reason: 'unavailable' };
    }
  }, [base]);

  // 093 check 16: the consultant's notes are saved on their own route. The
  // generated part is never sent, so a save can never overwrite it; the etag is
  // the page the notes were opened against.
  const saveNotes = useCallback(async (
    path: string,
    notes: string,
    etag: string,
  ): Promise<KnowledgeSaveResult> => {
    if (!base || !path) return { ok: false, reason: 'unavailable' };
    try {
      const res = await fetch(`${base}/file-notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'If-Match': etag },
        body: JSON.stringify({ path, notes }),
      });
      const body = await readJson(res);
      if (res.status === 409) {
        const current = (body as { current?: { content?: unknown; etag?: unknown } } | null)?.current;
        return {
          ok: false,
          reason: 'conflict',
          current: {
            content: typeof current?.content === 'string' ? current.content : '',
            etag: typeof current?.etag === 'string' ? current.etag : '',
          },
        };
      }
      const nextEtag = (body as { etag?: unknown } | null)?.etag;
      if (!res.ok || typeof nextEtag !== 'string') return { ok: false, reason: 'unavailable' };
      return { ok: true, etag: nextEtag };
    } catch {
      return { ok: false, reason: 'unavailable' };
    }
  }, [base]);

  const refileActivity = useCallback(async (
    activityId: string,
  ): Promise<ActivityRefileResult> => {
    if (!base || !activityId) return { ok: false, reason: 'unavailable' };
    try {
      const res = await fetch(
        `${base}/activities/${encodeURIComponent(activityId)}/file`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
      );
      if (!res.ok) return { ok: false, reason: 'unavailable' };
      const filing = readFiling(await readJson(res));
      if (!filing) return { ok: false, reason: 'unavailable' };
      return { ok: true, filing };
    } catch {
      return { ok: false, reason: 'unavailable' };
    }
  }, [base]);

  // Where the bytes of a kept file live. The view links straight at it, so the
  // browser opens or downloads the original with its own content type.
  const rawFileUrl = useCallback((path: string): string => (
    base && path ? `${base}/files/${encodeKnowledgePath(path)}?raw=1` : ''
  ), [base]);

  return {
    files, listStatus, refresh, openFile, saveFile, saveNotes, refileActivity, rawFileUrl,
  };
}
